/**
 * Client-side activation event emitter (Phase 3).
 *
 * Mirrors `sendSignupAttribution`: best-effort, never throws, never blocks the
 * UI. Each call invokes the authenticated `recordActivationEvent` callable
 * (the Cloud Function does all scoring / milestone / notification work). The
 * client only emits simple, non-sensitive product actions plus aggregate
 * counts — NEVER student names, emails, grades, or any classroom content.
 *
 * `firebase/functions` is imported lazily so it stays out of the auth-path
 * critical bundle.
 */
import { app, auth } from './firebase';

/** Action events the client may emit (must match the Cloud Function model). */
export type ActivationEventType =
  | 'USER_SIGNED_UP'
  | 'GOOGLE_CONNECTED'
  | 'COURSES_IMPORTED'
  | 'ASSIGNMENT_SELECTED'
  | 'RUBRIC_CREATED'
  | 'SCAN_COMPLETED'
  | 'FEEDBACK_GENERATED'
  | 'GRADE_RETURNED'
  | 'DAY_ACTIVE';

/**
 * Aggregate-only + teacher-identity payload. NO student-identifying data is
 * ever permitted. Allowed: aggregate counts, the teacher's own connected Google
 * account email/name, and teacher-owned Classroom course names.
 */
export interface ActivationEventData {
  courseCount?: number;
  studentCount?: number;
  paperCount?: number;
  /** Teacher's connected Google account email (allowed — not student data). */
  googleAccountEmail?: string;
  /** Teacher's connected Google account display name (allowed). */
  googleAccountDisplayName?: string;
  /** Teacher-owned Classroom course names (allowed — NOT student data). */
  courseNames?: string[];
}

export interface RecordActivationEventOptions {
  /**
   * When true, emit at most once per signed-in user per event type (guarded in
   * localStorage). Use for one-shot signals like GOOGLE_CONNECTED that the UI
   * may otherwise re-fire on every status refresh.
   */
  once?: boolean;
  /** Explicit idempotency id; otherwise one is generated for `once` events. */
  clientEventId?: string;
}

const STORAGE_PREFIX = 'dg_activation_v1';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function onceKey(uid: string, eventType: ActivationEventType): string {
  return `${STORAGE_PREFIX}:${uid}:${eventType}`;
}

function alreadyEmittedOnce(uid: string, eventType: ActivationEventType): boolean {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(onceKey(uid, eventType)) === '1';
  } catch {
    return false;
  }
}

function markEmittedOnce(uid: string, eventType: ActivationEventType): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(onceKey(uid, eventType), '1');
  } catch {
    // best-effort only
  }
}

function makeClientEventId(uid: string, eventType: ActivationEventType): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${uid}:${eventType}:${Date.now()}:${rand}`;
}

/** Drop any non-finite / negative count so only clean aggregates go to the server. */
function cleanCount(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined;
  return Math.floor(value);
}

/** Trimmed non-empty string (capped), or undefined. */
function cleanString(value: string | undefined, maxLen = 254): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, maxLen) : undefined;
}

/** Trimmed, de-duped, capped list of teacher-owned course names (or undefined). */
function cleanCourseNames(value: string[] | undefined): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const name = entry.trim().slice(0, 100);
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
    if (out.length >= 50) break;
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Emit an activation event for the current signed-in user. Returns true when a
 * call was actually sent (false when skipped: no user, duplicate `once`, etc.).
 */
export async function recordActivationEvent(
  eventType: ActivationEventType,
  data: ActivationEventData = {},
  options: RecordActivationEventOptions = {}
): Promise<boolean> {
  const uid = auth.currentUser?.uid;
  if (!uid) return false;

  if (options.once && alreadyEmittedOnce(uid, eventType)) return false;

  const payload: Record<string, unknown> = { eventType };
  const clientEventId =
    options.clientEventId ?? (options.once ? makeClientEventId(uid, eventType) : undefined);
  if (clientEventId) payload.clientEventId = clientEventId;

  const courseCount = cleanCount(data.courseCount);
  const studentCount = cleanCount(data.studentCount);
  const paperCount = cleanCount(data.paperCount);
  const googleAccountEmail = cleanString(data.googleAccountEmail);
  const googleAccountDisplayName = cleanString(data.googleAccountDisplayName, 128);
  const courseNames = cleanCourseNames(data.courseNames);
  if (courseCount !== undefined) payload.courseCount = courseCount;
  if (studentCount !== undefined) payload.studentCount = studentCount;
  if (paperCount !== undefined) payload.paperCount = paperCount;
  if (googleAccountEmail !== undefined) payload.googleAccountEmail = googleAccountEmail;
  if (googleAccountDisplayName !== undefined)
    payload.googleAccountDisplayName = googleAccountDisplayName;
  if (courseNames !== undefined) payload.courseNames = courseNames;

  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const callable = httpsCallable(getFunctions(app), 'recordActivationEvent');
    await callable(payload);
    if (options.once) markEmittedOnce(uid, eventType);
    return true;
  } catch {
    // Best-effort only — activation telemetry must never disrupt the app.
    return false;
  }
}

/** Args for a `COURSES_IMPORTED` emit, or null when there is nothing to send. */
export interface CoursesImportedEvent {
  eventType: 'COURSES_IMPORTED';
  data: ActivationEventData;
  options: RecordActivationEventOptions;
}

/**
 * Pure: build the `COURSES_IMPORTED` emit args from a course count and the
 * teacher's own course names. Returns null for a non-positive / non-finite
 * count (no successful import to report).
 *
 * Sends the aggregate `courseCount` plus teacher-owned `courseNames` (allowed
 * metadata) ONLY — never student rosters, emails, grades, or any classroom
 * content. The `clientEventId` is keyed by the count so a re-sync with the same
 * total de-dupes server-side while a growing total still updates the profile's
 * monotonic count.
 */
export function buildCoursesImportedEvent(
  courseCount: number,
  courseNames?: string[]
): CoursesImportedEvent | null {
  if (!Number.isFinite(courseCount) || courseCount <= 0) return null;
  const count = Math.floor(courseCount);
  const names = cleanCourseNames(courseNames);
  return {
    eventType: 'COURSES_IMPORTED',
    data: names ? { courseCount: count, courseNames: names } : { courseCount: count },
    options: { clientEventId: `courses-imported:${count}` },
  };
}

/**
 * Emit `COURSES_IMPORTED` after a *successful* Classroom sync/import. No-op when
 * `courseCount` is non-positive. Best-effort; never throws. `courseNames` are
 * the teacher's own Classroom course names (allowed metadata).
 */
export async function recordCoursesImported(
  courseCount: number,
  courseNames?: string[]
): Promise<boolean> {
  const spec = buildCoursesImportedEvent(courseCount, courseNames);
  if (!spec) return false;
  return recordActivationEvent(spec.eventType, spec.data, spec.options);
}
